/**
 * API Gateway Permissions
 * Granular permissions for business-facing API resources
 *
 * Nested object structure for better autocomplete and logical grouping.
 * Usage: Permission.documents.read, Permission.settings.webhooks.delete
 *
 * @see Issue #383 - Permission-Aware UI Components with Lock Icon Pattern
 */

// ============================================================================
// Nested Permission Object
// ============================================================================

/**
 * Permission namespace with nested structure for better DX.
 *
 * @example
 * ```ts
 * // Type autocomplete shows only relevant permissions
 * Permission.documents.read     // → 'documents:read'
 * Permission.settings.webhooks.delete  // → 'settings:webhooks:delete'
 *
 * // Use in components (security props built into base components)
 * <GlassButton permission={Permission.documents.delete}>
 * <GlassIconButton permission={Permission.settings.pipelines.delete}>
 *
 * // Use in decorators
 * @RequirePermissions(Permission.rulesets.create)
 * ```
 */
export const Permission = {
  // Agents
  agents: {
    read: 'agents:read',
    create: 'agents:create',
    update: 'agents:update',
    delete: 'agents:delete',
    deploy: 'agents:deploy',
    configure: 'agents:configure',
  },

  // Conversations
  conversations: {
    read: 'conversations:read',
    create: 'conversations:create',
    delete: 'conversations:delete',
  },

  // Messages
  messages: {
    read: 'messages:read',
    create: 'messages:create',
  },

  // Documents
  documents: {
    read: 'documents:read',
    write: 'documents:write',
    upload: 'documents:upload',
    delete: 'documents:delete',
    analyze: 'documents:analyze',
    verify: 'documents:verify', // Verify, reject, request re-upload
    sensitiveRead: 'documents:sensitive_read', // Tax returns, bank statements
    restrictedRead: 'documents:restricted_read', // SSN, full credit reports
  },

  // Document Intelligence - Rulesets
  rulesets: {
    read: 'rulesets:read',
    create: 'rulesets:create',
    update: 'rulesets:update',
    delete: 'rulesets:delete',
  },

  // Document Intelligence - Findings
  findings: {
    read: 'findings:read',
    update: 'findings:update',
  },

  // Knowledge Base
  knowledgeBase: {
    read: 'knowledge_base:read',
    create: 'knowledge_base:create',
    update: 'knowledge_base:update',
    delete: 'knowledge_base:delete',
  },

  // User Management (within business)
  users: {
    read: 'users:read',
    create: 'users:create',
    update: 'users:update',
    delete: 'users:delete',
  },

  // API Keys (for programmatic access)
  apiKeys: {
    read: 'api_keys:read',
    create: 'api_keys:create',
    revoke: 'api_keys:revoke',
  },

  // Integrations (OAuth apps, webhooks)
  integrations: {
    read: 'integrations:read',
    create: 'integrations:create',
    update: 'integrations:update',
    delete: 'integrations:delete',
  },

  // Entity Framework — custom-entity definitions, fields, and relationships
  // (Service 32). Records (CRUD over individual rows) are governed by the
  // EntityMembership-based access checks in the gateway and don't need
  // separate UI gates here.
  entities: {
    read: 'entities:read',
    create: 'entities:create',
    update: 'entities:update',
    delete: 'entities:delete',
    fields: {
      create: 'entities:fields:create',
      update: 'entities:fields:update',
      delete: 'entities:fields:delete',
      reorder: 'entities:fields:reorder',
    },
    relationships: {
      create: 'entities:relationships:create',
      delete: 'entities:relationships:delete',
    },
  },

  // Business Settings (nested for granular control)
  settings: {
    read: 'settings:read',
    update: 'settings:update',
    webhooks: {
      create: 'settings:webhooks:create',
      update: 'settings:webhooks:update',
      delete: 'settings:webhooks:delete',
    },
    pipelines: {
      create: 'settings:pipelines:create',
      update: 'settings:pipelines:update',
      delete: 'settings:pipelines:delete',
    },
    commissions: {
      create: 'settings:commissions:create',
      update: 'settings:commissions:update',
      delete: 'settings:commissions:delete',
    },
    astor: {
      update: 'settings:astor:update',
      delete: 'settings:astor:delete',
    },
    // Loan Lifecycle Subsystem (Issue #1388 — strict isolation from Pipeline)
    loanProducts: {
      read: 'settings:loan_products:read',
      create: 'settings:loan_products:create',
      update: 'settings:loan_products:update',
      delete: 'settings:loan_products:delete',
      admin: 'settings:loan_products:admin',
    },
    loanStages: {
      read: 'settings:loan_stages:read',
      create: 'settings:loan_stages:create',
      update: 'settings:loan_stages:update',
      delete: 'settings:loan_stages:delete',
      admin: 'settings:loan_stages:admin',
    },
    loanSubstages: {
      read: 'settings:loan_substages:read',
      create: 'settings:loan_substages:create',
      update: 'settings:loan_substages:update',
      delete: 'settings:loan_substages:delete',
    },
    loanStageTransitions: {
      read: 'settings:loan_stage_transitions:read',
      create: 'settings:loan_stage_transitions:create',
      delete: 'settings:loan_stage_transitions:delete',
    },
    loanStageRequirements: {
      read: 'settings:loan_stage_requirements:read',
      update: 'settings:loan_stage_requirements:update',
    },
    loanDataPoints: {
      read: 'settings:loan_data_points:read',
      create: 'settings:loan_data_points:create',
      update: 'settings:loan_data_points:update',
      delete: 'settings:loan_data_points:delete',
    },
    // Borrower Tier classifier rules (Issue #1469 — derived Tier 1/2/3)
    borrowerTierRules: {
      read: 'settings:borrower_tier_rules:read',
      create: 'settings:borrower_tier_rules:create',
      update: 'settings:borrower_tier_rules:update',
      delete: 'settings:borrower_tier_rules:delete',
    },
    // Warehouse Lines / Capital Providers (Issue #1631 — Multi-fund management)
    capitalProviders: {
      read: 'settings:capital_providers:read',
      create: 'settings:capital_providers:create',
      update: 'settings:capital_providers:update',
      delete: 'settings:capital_providers:delete',
    },
    warehouseLines: {
      read: 'settings:warehouse_lines:read',
      create: 'settings:warehouse_lines:create',
      update: 'settings:warehouse_lines:update',
      delete: 'settings:warehouse_lines:delete',
    },
    warehouseLineProfiles: {
      read: 'settings:warehouse_line_profiles:read',
      create: 'settings:warehouse_line_profiles:create',
      update: 'settings:warehouse_line_profiles:update',
      // Lifecycle (D1 SCD2): publish = activate the version (DRAFT/SCHEDULED → ACTIVE,
      // supersedes prior ACTIVE in same chain). Archive = ARCHIVED.
      publish: 'settings:warehouse_line_profiles:publish',
      archive: 'settings:warehouse_line_profiles:archive',
    },
    warehouseLineGlobalRules: {
      read: 'settings:warehouse_line_global_rules:read',
      create: 'settings:warehouse_line_global_rules:create',
      update: 'settings:warehouse_line_global_rules:update',
      delete: 'settings:warehouse_line_global_rules:delete',
    },
    warehouseLineSeeds: {
      // ListSeeds is platform-scoped; ApplyToBusiness writes into the caller's tenant
      applyToBusiness: 'settings:warehouse_line_seeds:apply_to_business',
    },
  },

  // Warehouse-line exception desk (Issue #1631 / D3) — request flow + approver inbox
  // Approval gate: blocking rules in the credit box → exception request → approval
  // moves the loan forward.
  warehouseLineExceptions: {
    read: 'warehouse_line_exceptions:read',
    request: 'warehouse_line_exceptions:request',
    approve: 'warehouse_line_exceptions:approve',
    deny: 'warehouse_line_exceptions:deny',
  },

  // Conditions to Fund (Issue #1631 / D10) — distinct from required docs
  caseConditionsToFund: {
    read: 'case_conditions_to_fund:read',
    create: 'case_conditions_to_fund:create',
    update: 'case_conditions_to_fund:update',
    satisfy: 'case_conditions_to_fund:satisfy',
    waive: 'case_conditions_to_fund:waive',
  },

  // Sizing runs (Issue #1631 / D8) — multi-fund fan-out invocation history
  sizingRuns: {
    read: 'sizing_runs:read',
    start: 'sizing_runs:start',
    select: 'sizing_runs:select', // pin a sized result to the case
  },

  // Analytics & Usage
  analytics: {
    read: 'analytics:read',
    export: 'analytics:export',
  },

  // Audit Logs (for business's own activity)
  audit: {
    read: 'audit:read',
    export: 'audit:export',
  },

  // GDPR operations (Issue #1296 — Article 17 right to erasure)
  // gdpr.eraseFromAudit is the ONLY permission that authorizes unsealing and
  // rewriting fields on a sealed audit_logs row; it should be granted to
  // super_admin / platform_admin only and attested on every invocation.
  gdpr: {
    eraseFromAudit: 'gdpr:erase_from_audit',
  },

  // Billing (business's own billing)
  billing: {
    read: 'billing:read',
    update: 'billing:update',
  },

  // Notifications
  notifications: {
    read: 'notifications:read',
    readAll: 'notifications:read_all', // Admin: read all business notifications
    create: 'notifications:create',
    update: 'notifications:update',
    delete: 'notifications:delete',
    rules: {
      read: 'notification_rules:read',
      create: 'notification_rules:create',
      update: 'notification_rules:update',
      delete: 'notification_rules:delete',
    },
    templates: {
      read: 'notification_templates:read',
      create: 'notification_templates:create',
      update: 'notification_templates:update',
      delete: 'notification_templates:delete',
    },
  },

  // Loan Operations (License-gated)
  loan: {
    view: 'loan:view', // Issue #1483 Gap 2 — read loan-level data (status, terms, summary)
    originate: 'loan:originate', // Requires NMLS_INDIVIDUAL or STATE_LOAN_OFFICER
    process: 'loan:process', // Requires STATE_LOAN_OFFICER
    underwrite: 'loan:underwrite', // Requires specific business license
    approve: 'loan:approve', // Issue #1483 Gap 2 — final approval gate (separate from underwrite)
    fund: 'loan:fund', // Requires STATE_LENDER business license
    service: 'loan:service', // Requires STATE_SERVICER business license
    modify: 'loan:modify', // Issue #617 - Request and apply loan modifications
    approveModification: 'loan:approve_modification', // Issue #617 - Approve/reject modifications

    // Issue #1557 — BIC role-permission matrix gates. Wire-format strings keep
    // their multi-colon, camelCase-action shape (e.g. 'loan:termSheet:send')
    // because they are referenced by string from `BicStageTransitionGuard`,
    // approval-workflow trigger actions, and the role-permission matrix.
    // Keys flatten the colon path to camelCase to avoid nesting (which would
    // collide with the existing string entries above, e.g. `loan.fund`).
    termSheetDraft: 'loan:termSheet:draft',
    termSheetSend: 'loan:termSheet:send', // Risk-bearing — requires approval workflow
    termSheetApprove: 'loan:termSheet:approve',
    appraisalOrder: 'loan:appraisal:order',
    titleOrder: 'loan:title:order',
    creditHardPull: 'loan:credit:hardPull',
    underwritingClearSubstage: 'loan:underwriting:clearSubstage',
    approvalConditionalIssue: 'loan:approval:conditional:issue',
    approvalConditionalApprove: 'loan:approval:conditional:approve',
    approvalFinalIssue: 'loan:approval:final:issue',
    approvalFinalApprove: 'loan:approval:final:approve',
    sizerOverride: 'loan:sizer:override',
    sizerOverrideApprove: 'loan:sizer:override:approve',
    rateOutOfBand: 'loan:rate:outOfBand',
    rateOutOfBandApprove: 'loan:rate:outOfBand:approve',
    declineSend: 'loan:decline:send',
    declineApprove: 'loan:decline:approve',
    closingGenerateDocs: 'loan:closing:generateDocs',
    closingSchedule: 'loan:closing:schedule',
    closingDraftWire: 'loan:closing:draftWire',
    fundWire: 'loan:fund:wire',
    fundWireApprove: 'loan:fund:wire:approve',
    modificationInitiate: 'loan:modification:initiate',
    modificationApprove: 'loan:modification:approve',
    refundInitiate: 'loan:refund:initiate',
    refundApprove: 'loan:refund:approve',
    drawInspect: 'loan:draw:inspect',
    drawApproveRelease: 'loan:draw:approveRelease',
  },

  // Credit Memo (Issue #1466 — cascade-resource introduced by epic #1460)
  // Issue #1483 Gap 6 — explicit gates required for read/write/submit/approve.
  creditMemo: {
    read: 'creditMemo:read',
    write: 'creditMemo:write',
    submit: 'creditMemo:submit',
    approve: 'creditMemo:approve',
  },

  // Case Comparable (Issue #1483 Gap 6 — cascade resource for epic #1460)
  caseComparable: {
    read: 'caseComparable:read',
    write: 'caseComparable:write',
  },

  // Borrower Exit (Issue #1483 Gap 6 — cascade resource for epic #1460)
  borrowerExit: {
    read: 'borrowerExit:read',
    write: 'borrowerExit:write',
  },

  // Release Schedule (Issue #1483 Gap 6 — cascade resource for epic #1460)
  releaseSchedule: {
    read: 'releaseSchedule:read',
    write: 'releaseSchedule:write',
    markReleased: 'releaseSchedule:mark_released',
  },

  // Borrower Tier (Issue #1469 / #1483 Gap 6 — classifier output drives pricing)
  borrowerTier: {
    read: 'borrowerTier:read',
    classify: 'borrowerTier:classify',
    override: 'borrowerTier:override',
  },

  // Case Vendor (Issue #1483 Gap 6 — cascade resource for epic #1460)
  caseVendor: {
    read: 'caseVendor:read',
    write: 'caseVendor:write',
  },

  // Applicability Rule (Issue #1483 Gap 6 — cascade resource for epic #1460)
  applicabilityRule: {
    read: 'applicabilityRule:read',
    write: 'applicabilityRule:write',
  },

  // Consumer Data Access (License-gated for compliance)
  consumer: {
    piiAccess: 'consumer:pii_access', // Requires NMLS license
    creditPull: 'consumer:credit_pull', // Requires NMLS + specific permissions
    financialsView: 'consumer:financials_view', // Requires NMLS license
    impersonate: 'consumers:impersonate', // Issue #321 - Customer support
  },

  // Consumer Intake Submissions (Issue #471)
  consumers: {
    read: 'consumers:read',
    create: 'consumers:create',
    update: 'consumers:update',
    delete: 'consumers:delete',
    /**
     * Send an application-invite (a.k.a. pre-qualification email) to a
     * consumer. Separate from `update` so orgs can grant loan officers the
     * ability to invite prospective borrowers without granting full edit
     * rights over existing consumer records.
     */
    invite: 'consumers:invite',
  },

  // Case Management
  cases: {
    read: 'cases:read',
    create: 'cases:create',
    update: 'cases:update',
    delete: 'cases:delete',
    assign: 'cases:assign',
    // Issue #1645 / Issue #1631 D2 — manual pin escape hatch.
    // Distinct from sizingRuns.select because manual pin bypasses sizer rule
    // evaluation (no credit-box / global-rule check) and emits its own audit
    // event class (`case.warehouse_line_manually_pinned`). Granted to risk /
    // admin roles only (NOT loan_officer); narrower than sizingRuns.select.
    manualPinWarehouseLine: 'cases:manual_pin_warehouse_line',
    // Issue #1645 — clears warehouse_line_id + loan_type_profile_version_id
    // off the case row. Audited as `case.warehouse_line_unpinned` with
    // optional `metadata.unpinReason` (D4 — reason optional). Mirrors the
    // role-set that can pin a case (sizingRuns.select).
    unpinWarehouseLine: 'cases:unpin_warehouse_line',
  },

  // Pipeline Stage Transitions
  pipeline: {
    stageTransition: 'pipeline:stage_transition', // Base permission for stage moves
    dualControlApprove: 'pipeline:dual_control_approve', // Approve dual-control transactions
    amountOverride: 'pipeline:amount_override', // Override amount thresholds
    // Issue #1557 — BIC role-permission matrix: ops_manager escalates stalled
    // applications. Wire string is camelCase-free; matches the matrix.
    escalate: 'pipeline:escalate',
  },

  // Issue #1557 — BIC role-permission matrix.
  // Servicing operations on funded loans (post-funding portfolio servicing).
  // Owned by SERVICING + PORTFOLIO_MANAGER roles.
  servicing: {
    read: 'servicing:read',
    processPayment: 'servicing:processPayment',
    issuePayoff: 'servicing:issuePayoff',
    sendReminder: 'servicing:sendReminder',
    modify: 'servicing:modify', // Servicing-side modification (fees, escrow, etc.)
  },

  // Issue #1557 — BIC role-permission matrix.
  // Comparable-sales lookups for the analyst role.
  comps: {
    read: 'comps:read',
  },

  // Issue #1557 — BIC role-permission matrix.
  // Ad-hoc report execution (analyst role). Distinct from `reports:read`
  // exposed via the API-scope vocabulary in `constants/api-scopes.ts`.
  reports: {
    run: 'reports:run',
  },

  // Issue #1557 — BIC role-permission matrix.
  // Capital allocation (Fund Manager role).
  capital: {
    read: 'capital:read',
    approveLargeLoan: 'capital:approveLargeLoan',
  },

  // Issue #1557 — BIC role-permission matrix.
  // Portfolio analytics (Portfolio Manager role).
  portfolio: {
    analyze: 'portfolio:analyze',
  },

  // Team Management (Extended)
  teams: {
    create: 'teams:create',
    update: 'teams:update',
    delete: 'teams:delete',
    manageMembers: 'teams:manage_members',
  },

  // Partner Management (Issue #484)
  partners: {
    read: 'partners:read', // View partner invitations and relationships
    invite: 'partners:invite', // Create partner invitations
    manage: 'partners:manage', // Update status, cancel, resend invitations
    delete: 'partners:delete', // Delete partner relationships
    refer: 'partners:refer', // Push outbound referrals to destination lenders (Issue #1735)
    agreements: {
      read: 'partners:agreements:read', // View partner agreements
      create: 'partners:agreements:create', // Create partner agreements
      update: 'partners:agreements:update', // Modify partner agreements
      approve: 'partners:agreements:approve', // Approve partner agreements
    },
  },

  // Loan Sizer (Issue #1342/#1343 — Service 33 pricing-engine)
  loanSizer: {
    read: 'loan_sizer:read', // View saved sizings, list, get prefill for a case
    write: 'loan_sizer:write', // Run sizer, save records, convertToCase, applyToCase
    manageRules: 'loan_sizer:manage_rules', // Admin: edit SizerProfile + SizerAdjustment + RateTier + PointsTier
    managePolicy: 'loan_sizer:manage_policy', // Admin: edit PlatformSizingPolicy (Issue #1438/#1340)
    viewRules: 'loan_sizer:view_rules', // Read-only formula viewer (/pricing/sizing-policy) — Issue #1438
  },

  // Assessment & Digital Twin (Service 18)
  assessment: {
    view: 'assessment:view',
    creditPull: 'assessment:credit_pull',
    override: 'assessment:override',
    twinManage: 'assessment:twin_manage',
    twinAnalytics: 'assessment:twin_analytics',
  },

  // E-Signatures (Issue #56, split for multi-signer in Issue #1304)
  esign: {
    read: 'esign:read', // View templates, submissions, retained copies
    send: 'esign:send', // Create + send envelopes (replaces the creation half of esign:create)
    sign: 'esign:sign', // Appear as a lender-side signer (lender_signer or credit_manager)
    void: 'esign:void', // Void/cancel submissions
    download: 'esign:download', // Download signed documents
    // Issue #1531 Phase 6 — admin-only envelope expiration extension (≤30 days,
    // requires reason text). Per v4 plan §6.1 deadline overrides are policy
    // decisions and must NOT be granted to standard `esign.send` operators;
    // wildcard admin roles (super_admin / platform_admin / business_admin) pick
    // this up via `Permission.all`. Operational roles (loan_officer, closer,
    // processor, underwriter) intentionally do NOT receive this gate.
    extend: 'esign:extend',
  },

  // System Templates (Issue #400)
  templates: {
    read: 'templates:read', // View system templates
    create: 'templates:create', // Create entities from templates
    sequences: {
      read: 'templates:sequences:read',
      create: 'templates:sequences:create',
    },
    campaigns: {
      read: 'templates:campaigns:read',
      create: 'templates:campaigns:create',
    },
    pipelines: {
      read: 'templates:pipelines:read',
      create: 'templates:pipelines:create',
    },
    savedSearches: {
      read: 'templates:saved_searches:read',
      create: 'templates:saved_searches:create',
    },
    termsheets: {
      read: 'templates:termsheets:read',
      create: 'templates:termsheets:create',
      // Issue #1307 — admin template CRUD; referenced from
      // 2-api-gateway/src/trpc/routers/termsheets.router.ts.
      update: 'templates:termsheets:update',
      delete: 'templates:termsheets:delete',
    },
    // Issue #1531 Phase 7 — admin-only sensitive-key override management.
    // Per v4 plan §6.2 the platform applies a category-level block on
    // PII-sensitive canonical keys (ssn / dob / credit_score) at envelope-Generate
    // time. A handful of legitimate templates need to opt out of that block
    // (e.g. a tax-form template that legally requires SSN binding). This gate
    // authorizes the per-template opt-out flow:
    //   - flips status=DRAFT (forces re-publish via designer)
    //   - audit-logs the override with a reason text + 7-year retention
    // Granted to platform-admin / compliance officers only. Wildcard admin
    // roles (super_admin / platform_admin / business_admin) pick this up via
    // `Permission.all`. Operational roles (loan_officer, processor, closer,
    // underwriter, business_user) intentionally do NOT receive this gate —
    // it is a platform-side compliance control, not a tenant-side one.
    documentTemplates: {
      sensitiveOverride: 'templates:documentTemplates:sensitiveOverride',
    },
  },

  // Custom Domain Management
  customDomain: {
    manage: 'custom_domain:manage',
    approve: 'custom_domain:approve',
  },

  // Platform Defaults (Issue #1522 — fork/revert/delete subsystem for epic #1460 AC#4)
  // Platform-managed defaults stored in dedicated template tables
  // (`LoanDataPointTemplate`, `BorrowerTierRuleTemplate`) — NOT tenant rows
  // with businessId IS NULL. Granted to super_admin / platform_admin only.
  // Tenant-side operations (fork/revertToTemplate/deleteInstance on the
  // tenant's OWN copies) are gated by the matching settings.* permissions,
  // not these.
  platformDefaults: {
    read: 'platform_defaults:read',
    write: 'platform_defaults:write',
  },

  // Approval workflow admin (Issue #1559 — Stream D / Phase 5a)
  // Governs the per-tenant approval-workflow editor. Read = view escalation
  // chains + SLA targets; edit = mutate workflow rules. Edit grants belong to
  // SUPER_ADMIN / PLATFORM_ADMIN; BUSINESS_ADMIN gets read.
  approvalWorkflows: {
    read: 'approvalWorkflows:read',
    edit: 'approvalWorkflows:edit',
  },

  // Stage permission admin (Issue #1559 — Stream D / Phase 5b)
  // Per-stage RBAC: who can move loans through each pipeline stage, dual-approval
  // toggles, dollar-threshold overrides. Edit grants belong to SUPER_ADMIN /
  // PLATFORM_ADMIN; BUSINESS_ADMIN gets read.
  stagePermissions: {
    read: 'stagePermissions:read',
    edit: 'stagePermissions:edit',
  },

  // Field-level data-point permissions (Issue #1560)
  // Role-based view/edit/hide attribution per LoanDataPoint canonical key,
  // optionally refined per stage. Edit grants belong to SUPER_ADMIN /
  // PLATFORM_ADMIN; BUSINESS_ADMIN gets read.
  fieldPermissions: {
    read: 'fieldPermissions:read',
    edit: 'fieldPermissions:edit',
  },

  // KPI Scorecard (Issue #1389)
  // Scope-aware (business | team | partner) — partner-scoped writes also
  // require the actor to have access to the partner via partners.manage or
  // CRM partner-manager assignment. Enforced at service layer.
  scorecard: {
    read: 'scorecard:read',
    create: 'scorecard:create',
    update: 'scorecard:update',
    delete: 'scorecard:delete',
  },

  // Close.com Integration (Issue #1621 — Phase 2 Stream 3)
  // Used by `closeIntegration` tRPC router to gate connection management,
  // backfill execution, per-consumer refresh, and dedup-merge actions.
  closeIntegration: {
    /** Save / update / disconnect the Close API key + custom-field config. */
    configure: 'closeIntegration:configure',
    /** Kick off / monitor a full backfill sync job. */
    backfill: 'closeIntegration:backfill',
  },

  // Close.com synced consumers (Issue #1621 — Phase 2 Stream 3)
  closeConsumer: {
    /** Re-pull a single consumer + comms from Close on-demand. */
    refresh: 'closeConsumer:refresh',
    /** Merge two consumer rows when dedup flags a probable duplicate. */
    merge: 'closeConsumer:merge',
  },

  // Close.com per-tenant custom-field mapping (Issue #1621 — Phase 2 Stream 4e)
  // Used by `closeIntegration.fieldMappings` tRPC namespace to gate listing /
  // mutating the per-tenant mapping table.
  closeFieldMapping: {
    /** List existing mappings + the catalog of available fiopsTargets. */
    read: 'closeFieldMapping:read',
    /** Create / update / delete a mapping row. */
    write: 'closeFieldMapping:write',
  },

  // All permissions (business admin wildcard)
  all: '*',
} as const;

// ============================================================================
// Type Utilities
// ============================================================================

/**
 * Recursively extracts all string values from a nested object type.
 * Used to create a union type of all permission values.
 */
type DeepStringValues<T> = T extends string
  ? T
  : T extends object
    ? { [K in keyof T]: DeepStringValues<T[K]> }[keyof T]
    : never;

/**
 * Union type of all permission string values.
 * Use this when you need to type a permission variable.
 *
 * @example
 * ```ts
 * const perm: PermissionValue = Permission.documents.read; // ✅
 * const perm: PermissionValue = 'invalid:permission'; // ❌ Type error
 * ```
 */
export type PermissionValue = DeepStringValues<typeof Permission>;

/**
 * Type for the Permission object itself.
 * Useful for typing function parameters that accept the Permission namespace.
 */
export type PermissionType = typeof Permission;

// ============================================================================
// Role to Permissions Mapping
// ============================================================================

/** Consumer / Borrower permissions (shared between both role keys) */
const CONSUMER_PERMISSIONS: PermissionValue[] = [
  Permission.cases.read,
  Permission.documents.read,
  Permission.documents.upload,
  Permission.messages.read,
  Permission.messages.create,
  Permission.esign.read,
  Permission.esign.download,
  Permission.notifications.read,
];

/**
 * Role to permissions mapping for UserRole.
 * Uses the nested Permission object for type safety.
 */
export const USER_ROLE_PERMISSIONS: Record<string, PermissionValue[]> = {
  // Super Admin has all permissions across all businesses
  super_admin: [Permission.all],

  // Platform Admin has all permissions across all businesses
  platform_admin: [Permission.all],

  // Business Admin has all permissions within their business
  business_admin: [Permission.all],

  // Business User has most permissions except dangerous ones
  business_user: [
    // Case Management
    Permission.cases.read,
    Permission.cases.create,
    Permission.cases.update,
    Permission.cases.assign,
    // Note: cases.delete intentionally omitted — only business_admin can delete cases
    // Comparables & Borrower Exit History (Issue #1467) — closer / underwriter
    // workflow surfaces. Defensive grants on this branch; PR #1486 owns the
    // canonical role-permission table for these.
    Permission.caseComparable.read,
    Permission.caseComparable.write,
    Permission.borrowerExit.read,
    Permission.borrowerExit.write,
    Permission.agents.read,
    Permission.agents.create,
    Permission.agents.update,
    Permission.agents.deploy,
    Permission.conversations.read,
    Permission.conversations.create,
    Permission.conversations.delete,
    Permission.messages.read,
    Permission.messages.create,
    Permission.documents.read,
    Permission.documents.upload,
    Permission.documents.delete,
    Permission.documents.analyze,
    // Operational verify/reject/request-reupload action on data-room docs.
    // Per access-level-permissions.ts (`DOCUMENTS[EDIT]`), `documents.verify`
    // sits in the same tier as `upload`/`analyze` which `business_user` already
    // has — the omission here hid the verify/reject/reupload action buttons in
    // DataRoomTab for all business operational personas (seniorUnderwriter,
    // juniorUnderwriter, loanCloser, processor — all minted as `business_user`
    // in tests/cross-portal personas) because `useHasPermission(documents.verify)`
    // returned false, which is exactly what the
    // document-upload-approval-signing cross-portal spec tripped on.
    Permission.documents.verify,
    // Entity Framework (Service 32) — operational EDIT tier (mirrors
    // `documents.verify` grant). `ENTITY_FRAMEWORK[EDIT]` in
    // access-level-permissions.ts groups these together; omitting them from
    // business_user hides the entity create/update + field reorder + relationship
    // create affordances in the Entity Framework UI for operational personas.
    // ADMIN tier (entities.delete, fields.delete, relationships.delete) stays
    // restricted to business_admin / platform_admin / super_admin.
    Permission.entities.read,
    Permission.entities.create,
    Permission.entities.update,
    Permission.entities.fields.create,
    Permission.entities.fields.update,
    Permission.entities.fields.reorder,
    Permission.entities.relationships.create,
    Permission.rulesets.read,
    Permission.rulesets.create,
    Permission.rulesets.update,
    Permission.findings.read,
    Permission.findings.update,
    Permission.knowledgeBase.read,
    Permission.knowledgeBase.create,
    Permission.knowledgeBase.update,
    Permission.knowledgeBase.delete,
    Permission.users.read,
    Permission.apiKeys.read,
    Permission.apiKeys.create,
    Permission.integrations.read,
    Permission.integrations.create,
    Permission.integrations.update,
    // Close.com integration (Issue #1621) — business users can configure
    // their Close connection and act on synced consumers (refresh + merge).
    // Backfill is gated to business_admin to keep the long-running scrape
    // off everyone's account. Field-mapping READ is granted so business users
    // can view the per-tenant mappings configured by their admin (write stays
    // admin-only via business_admin: [Permission.all]).
    Permission.closeIntegration.configure,
    Permission.closeConsumer.refresh,
    Permission.closeConsumer.merge,
    Permission.closeFieldMapping.read,
    Permission.settings.read,
    Permission.analytics.read,
    Permission.audit.read,
    Permission.billing.read,
    // E-Signatures (Issue #56, split in #1304) - business users can manage e-sign.
    // Grant BOTH esign.send + esign.sign to preserve pre-split behaviour for operators
    // who previously held esign.create.
    Permission.esign.read,
    Permission.esign.send,
    Permission.esign.sign,
    Permission.esign.void,
    Permission.esign.download,
    // Consumers (Issue #1237 — inline-edit rollout)
    Permission.consumers.read,
    Permission.consumers.create,
    Permission.consumers.update,
    Permission.consumers.invite,
    // Partners (Issue #484) - business users can view, invite, and manage partners
    Permission.partners.read,
    Permission.partners.invite,
    Permission.partners.manage,
    Permission.partners.agreements.read,
    // Templates (Issue #400)
    Permission.templates.read,
    Permission.templates.create,
    Permission.templates.sequences.read,
    Permission.templates.sequences.create,
    Permission.templates.campaigns.read,
    Permission.templates.campaigns.create,
    Permission.templates.pipelines.read,
    Permission.templates.pipelines.create,
    Permission.templates.savedSearches.read,
    Permission.templates.savedSearches.create,
    Permission.templates.termsheets.read,
    Permission.templates.termsheets.create,
    // Issue #1307 — business users can edit templates they create; delete is
    // restricted to business_admin / platform_admin / super_admin.
    Permission.templates.termsheets.update,
    // Loan Sizer (Issue #1343) — business users can run and save sizings
    Permission.loanSizer.read,
    Permission.loanSizer.write,
    // Loan Sizer formula viewer (Issue #1438) — read-only access to resolved policy
    Permission.loanSizer.viewRules,
    // Scorecard (Issue #1389) — business users can read and record measurements
    Permission.scorecard.read,
    Permission.scorecard.create,
    Permission.scorecard.update,
    Permission.scorecard.delete,
    // Issue #1483 Gap 2/6 — loan-level view + cascade-resource reads (no writes)
    Permission.loan.view,
    Permission.creditMemo.read,
    Permission.caseComparable.read,
    Permission.borrowerExit.read,
    Permission.releaseSchedule.read,
    Permission.borrowerTier.read,
    Permission.caseVendor.read,
    Permission.applicabilityRule.read,
    // Loan-stage catalog + assessment view (Issue #1749 follow-up) — needed
    // to render the stage indicator + assessment fields on loan-detail pages.
    // Read-only; the `settings:` prefix is historical and does not imply
    // admin-only here. Write permissions on the stage catalog stay restricted.
    Permission.settings.loanStages.read,
    Permission.settings.loanStageTransitions.read,
    Permission.assessment.view,
  ],

  // Business Viewer has read-only access
  business_viewer: [
    // Case Management (read-only)
    Permission.cases.read,
    Permission.agents.read,
    Permission.conversations.read,
    Permission.messages.read,
    Permission.documents.read,
    Permission.rulesets.read,
    Permission.findings.read,
    Permission.knowledgeBase.read,
    Permission.users.read,
    Permission.apiKeys.read,
    Permission.integrations.read,
    Permission.settings.read,
    Permission.analytics.read,
    Permission.audit.read,
    Permission.billing.read,
    // E-Signatures (Issue #56) - viewers can read e-sign data
    Permission.esign.read,
    Permission.esign.download,
    // Consumers (Issue #1237) - read-only for viewers
    Permission.consumers.read,
    // Partners (Issue #484) - read-only for viewers
    Permission.partners.read,
    Permission.partners.agreements.read,
    // Templates (Issue #400) - read-only
    Permission.templates.read,
    Permission.templates.sequences.read,
    Permission.templates.campaigns.read,
    Permission.templates.pipelines.read,
    Permission.templates.savedSearches.read,
    Permission.templates.termsheets.read,
    // Loan Sizer (Issue #1343) — viewers can read but not write sizings
    Permission.loanSizer.read,
    // Loan Sizer formula viewer (Issue #1438) — read-only access to resolved policy
    Permission.loanSizer.viewRules,
    // Scorecard (Issue #1389) — viewers can read scorecards
    Permission.scorecard.read,
    // Issue #1483 Gap 2/6 — loan-level view + cascade-resource reads (read-only)
    Permission.loan.view,
    Permission.creditMemo.read,
    Permission.caseComparable.read,
    Permission.borrowerExit.read,
    Permission.releaseSchedule.read,
    Permission.borrowerTier.read,
    Permission.caseVendor.read,
    Permission.applicabilityRule.read,
  ],

  // ==================== Loan Operation Roles ====================

  // Underwriter (Senior) - full underwriting permissions
  underwriter: [
    Permission.cases.read,
    Permission.cases.update,
    Permission.cases.assign,
    Permission.documents.read,
    Permission.documents.analyze,
    Permission.documents.sensitiveRead,
    Permission.rulesets.read,
    Permission.findings.read,
    Permission.findings.update,
    Permission.consumers.read,
    Permission.consumer.piiAccess,
    Permission.consumer.financialsView,
    Permission.consumer.creditPull,
    Permission.loan.underwrite,
    Permission.loan.originate,
    Permission.pipeline.stageTransition,
    Permission.pipeline.dualControlApprove,
    Permission.notifications.read,
    Permission.analytics.read,
    Permission.templates.read,
    Permission.templates.termsheets.read,
    Permission.templates.termsheets.create,
    Permission.templates.termsheets.update,
    Permission.loanSizer.read,
    Permission.loanSizer.write,
    Permission.loanSizer.viewRules, // Issue #1438: read-only formula viewer
    // Issue #1483 Gap 2/6 — loan view + approve, cascade-resource reads,
    // credit memo write/submit/approve, borrower-tier override, applicability rule write
    Permission.loan.view,
    Permission.loan.approve,
    Permission.creditMemo.read,
    Permission.creditMemo.write,
    Permission.creditMemo.submit,
    Permission.creditMemo.approve,
    Permission.caseComparable.read,
    Permission.borrowerExit.read,
    Permission.releaseSchedule.read,
    Permission.borrowerTier.read,
    Permission.borrowerTier.override,
    Permission.caseVendor.read,
    Permission.caseVendor.write,
    Permission.applicabilityRule.read,
    Permission.applicabilityRule.write,
    // Warehouse-line management (Issue #1631) — underwriters size against lines,
    // request exceptions on blocking rules, and approve/deny credit-side
    // exception requests (the credit-risk dimension; ops_manager handles the
    // operational/compliance dimension and grants the same approve/deny).
    Permission.settings.warehouseLines.read,
    Permission.settings.warehouseLineProfiles.read,
    Permission.settings.warehouseLineGlobalRules.read,
    Permission.settings.capitalProviders.read,
    Permission.warehouseLineExceptions.read,
    Permission.warehouseLineExceptions.request,
    Permission.warehouseLineExceptions.approve,
    Permission.warehouseLineExceptions.deny,
    Permission.sizingRuns.read,
    Permission.sizingRuns.start,
    Permission.sizingRuns.select,
    // Issue #1645 — escape hatches for the pin lifecycle. Underwriters can
    // unpin (e.g. when a case is reassigned) and manually pin without sizing
    // (e.g. legacy / grandfathered loans where the line is already known).
    Permission.cases.unpinWarehouseLine,
    Permission.cases.manualPinWarehouseLine,
    Permission.caseConditionsToFund.read,
    // Underwriters originate CTFs as part of credit decisioning (the
    // BLOCK-rule waiver is what creates a CTF on the case).
    Permission.caseConditionsToFund.create,
    // Loan-stage catalog + assessment view (Issue #1749 follow-up) — needed
    // to render the stage indicator on every loan-detail page and to read
    // assessment outputs. Read-only; write permissions stay admin-only.
    Permission.settings.loanStages.read,
    Permission.settings.loanStageTransitions.read,
    Permission.assessment.view,
  ],

  // Analyst (Junior Underwriter) - read-heavy subset of underwriter
  analyst: [
    Permission.cases.read,
    Permission.documents.read,
    Permission.documents.analyze,
    Permission.rulesets.read,
    Permission.findings.read,
    Permission.consumers.read,
    Permission.consumer.financialsView,
    Permission.notifications.read,
    Permission.analytics.read,
    Permission.templates.read,
    Permission.templates.termsheets.read,
    Permission.loanSizer.read,
    Permission.loanSizer.viewRules, // Issue #1438: read-only formula viewer
    // Case Vendors (Issue #1470) — read-only access for analysts
    Permission.caseVendor.read,
    // Warehouse-line management (Issue #1631) — analysts read sizing runs to QC.
    Permission.settings.warehouseLines.read,
    Permission.settings.warehouseLineProfiles.read,
    Permission.settings.warehouseLineGlobalRules.read,
    Permission.settings.capitalProviders.read,
    Permission.sizingRuns.read,
    Permission.warehouseLineExceptions.read,
    Permission.caseConditionsToFund.read,
    // Loan-stage catalog + assessment view (Issue #1749 follow-up) — needed
    // to render the stage indicator on every loan-detail page.
    Permission.settings.loanStages.read,
    Permission.settings.loanStageTransitions.read,
    Permission.assessment.view,
  ],

  // Processor - manages case documentation and workflow
  processor: [
    Permission.cases.read,
    Permission.cases.update,
    Permission.documents.read,
    Permission.documents.upload,
    Permission.documents.write,
    Permission.documents.analyze,
    Permission.rulesets.read,
    Permission.findings.read,
    Permission.consumers.read,
    Permission.consumer.piiAccess,
    Permission.loan.process,
    Permission.pipeline.stageTransition,
    Permission.notifications.read,
    Permission.notifications.create,
    Permission.templates.read,
    // Case Vendors (Issue #1470) — processors set up vendors during processing
    Permission.caseVendor.read,
    Permission.caseVendor.write,
    // Warehouse-line management (Issue #1631) — processors track CTF progress
    // through closing; they don't size, but they own moving CTFs to satisfied.
    Permission.settings.warehouseLines.read,
    Permission.settings.warehouseLineProfiles.read,
    Permission.sizingRuns.read,
    Permission.caseConditionsToFund.read,
    Permission.caseConditionsToFund.update,
    Permission.caseConditionsToFund.satisfy,
    // Loan-stage catalog + assessment view (Issue #1749 follow-up) — needed
    // to render the stage indicator on every loan-detail page.
    Permission.settings.loanStages.read,
    Permission.settings.loanStageTransitions.read,
    Permission.assessment.view,
  ],

  // Closer - handles loan closing workflow
  closer: [
    Permission.cases.read,
    Permission.cases.update,
    Permission.documents.read,
    Permission.documents.upload,
    Permission.documents.write,
    Permission.consumers.read,
    Permission.consumer.piiAccess,
    Permission.loan.fund,
    Permission.pipeline.stageTransition,
    Permission.notifications.read,
    Permission.notifications.create,
    Permission.templates.read,
    Permission.templates.termsheets.read,
    // Issue #1483 Gap 2/6 — loan view + cascade-resource reads;
    // closers own caseVendor + caseComparable + releaseSchedule writes (Issue #1470)
    Permission.loan.view,
    Permission.creditMemo.read,
    Permission.caseComparable.read,
    Permission.caseComparable.write,
    Permission.borrowerExit.read,
    Permission.releaseSchedule.read,
    Permission.releaseSchedule.write,
    Permission.releaseSchedule.markReleased,
    Permission.borrowerTier.read,
    Permission.caseVendor.read,
    Permission.caseVendor.write,
    Permission.applicabilityRule.read,
    // Warehouse-line management (Issue #1631) — closers see warehouse-line
    // attribution on docs + own final CTF satisfaction at funding time.
    Permission.settings.warehouseLines.read,
    Permission.settings.warehouseLineProfiles.read,
    Permission.sizingRuns.read,
    Permission.caseConditionsToFund.read,
    Permission.caseConditionsToFund.update,
    Permission.caseConditionsToFund.satisfy,
    Permission.caseConditionsToFund.waive,
    // Loan-stage catalog + assessment view (Issue #1749 follow-up) — needed
    // to render the stage indicator on every loan-detail page.
    Permission.settings.loanStages.read,
    Permission.settings.loanStageTransitions.read,
    Permission.assessment.view,
  ],

  // Ops Manager (Compliance Officer) - audit and compliance focus
  ops_manager: [
    Permission.cases.read,
    Permission.documents.read,
    Permission.documents.sensitiveRead,
    Permission.documents.restrictedRead,
    Permission.rulesets.read,
    Permission.rulesets.update,
    Permission.findings.read,
    Permission.findings.update,
    Permission.consumers.read,
    Permission.consumer.piiAccess,
    Permission.audit.read,
    Permission.audit.export,
    Permission.analytics.read,
    Permission.analytics.export,
    Permission.notifications.read,
    Permission.settings.read,
    // Scorecard (Issue #1389) — ops managers track operational KPIs
    Permission.scorecard.read,
    Permission.scorecard.create,
    Permission.scorecard.update,
    Permission.scorecard.delete,
    // Case Vendors (Issue #1470) — ops/compliance need read access for audits
    Permission.caseVendor.read,
    // Warehouse-line management (Issue #1631) — ops/treasury role owns the
    // capital-markets configuration surface (capital providers, lines,
    // profile lifecycle, global rules, seed templates). Without these grants
    // the entire M1 write surface is super_admin / platform_admin / business_admin
    // only — loan officers see CTAs they cannot complete.
    Permission.settings.warehouseLines.read,
    Permission.settings.warehouseLines.create,
    Permission.settings.warehouseLines.update,
    Permission.settings.warehouseLines.delete,
    Permission.settings.capitalProviders.read,
    Permission.settings.capitalProviders.create,
    Permission.settings.capitalProviders.update,
    Permission.settings.capitalProviders.delete,
    Permission.settings.warehouseLineProfiles.read,
    Permission.settings.warehouseLineProfiles.create,
    Permission.settings.warehouseLineProfiles.update,
    Permission.settings.warehouseLineProfiles.publish,
    Permission.settings.warehouseLineProfiles.archive,
    Permission.settings.warehouseLineGlobalRules.read,
    Permission.settings.warehouseLineGlobalRules.create,
    Permission.settings.warehouseLineGlobalRules.update,
    Permission.settings.warehouseLineGlobalRules.delete,
    Permission.settings.warehouseLineSeeds.applyToBusiness,
    // Exception desk approver — ops/compliance approves cross-cutting risk
    // exceptions; underwriter approval is on the credit dimension.
    Permission.warehouseLineExceptions.read,
    Permission.warehouseLineExceptions.approve,
    Permission.warehouseLineExceptions.deny,
    Permission.sizingRuns.read,
    // Issue #1645 — pin lifecycle escape hatches. Risk/admin holds both
    // unpin and manual-pin grants because manual-pin (D2) bypasses sizer
    // rule evaluation and is intended as a controlled override.
    Permission.cases.unpinWarehouseLine,
    Permission.cases.manualPinWarehouseLine,
    Permission.caseConditionsToFund.read,
    // Loan-stage catalog + assessment view (Issue #1749 follow-up) — needed
    // to render the stage indicator on every loan-detail page and to audit
    // assessment outputs.
    Permission.settings.loanStages.read,
    Permission.settings.loanStageTransitions.read,
    Permission.assessment.view,
  ],

  // Viewer - read-only across all entities (audit/external reviewer)
  viewer: [
    Permission.cases.read,
    Permission.documents.read,
    Permission.rulesets.read,
    Permission.findings.read,
    Permission.consumers.read,
    Permission.agents.read,
    Permission.conversations.read,
    Permission.messages.read,
    Permission.knowledgeBase.read,
    Permission.analytics.read,
    Permission.audit.read,
    Permission.notifications.read,
    Permission.templates.read,
    Permission.templates.sequences.read,
    Permission.templates.campaigns.read,
    Permission.templates.pipelines.read,
    Permission.templates.savedSearches.read,
    Permission.templates.termsheets.read,
    Permission.partners.read,
    Permission.partners.agreements.read,
    // Scorecard (Issue #1389) — viewers can read scorecards
    Permission.scorecard.read,
    // Case Vendors (Issue #1470) — read-only access for viewers
    Permission.caseVendor.read,
    // Warehouse-line management (Issue #1631) — read-only attribution.
    Permission.settings.warehouseLines.read,
    Permission.settings.warehouseLineProfiles.read,
    Permission.sizingRuns.read,
    Permission.warehouseLineExceptions.read,
    Permission.caseConditionsToFund.read,
    // Loan-stage catalog + assessment view (Issue #1749 follow-up) — read-only
    // viewers still need to render the stage indicator on loan-detail pages.
    Permission.settings.loanStages.read,
    Permission.settings.loanStageTransitions.read,
    Permission.assessment.view,
  ],

  // Loan Officer - broad case/loan management
  loan_officer: [
    Permission.cases.read,
    Permission.cases.create,
    Permission.cases.update,
    Permission.cases.assign,
    Permission.documents.read,
    Permission.documents.upload,
    Permission.documents.write,
    Permission.documents.analyze,
    Permission.documents.sensitiveRead,
    Permission.rulesets.read,
    Permission.findings.read,
    Permission.findings.update,
    Permission.consumers.read,
    Permission.consumers.create,
    Permission.consumers.update,
    Permission.consumers.invite,
    Permission.consumer.piiAccess,
    Permission.consumer.creditPull,
    Permission.consumer.financialsView,
    Permission.loan.originate,
    Permission.loan.process,
    Permission.pipeline.stageTransition,
    Permission.notifications.read,
    Permission.notifications.create,
    Permission.analytics.read,
    Permission.templates.read,
    Permission.templates.create,
    Permission.templates.termsheets.read,
    Permission.templates.termsheets.create,
    Permission.templates.termsheets.update,
    Permission.partners.read,
    Permission.partners.invite,
    Permission.loanSizer.read,
    Permission.loanSizer.write,
    Permission.loanSizer.viewRules, // Issue #1438: read-only formula viewer
    // Scorecard (Issue #1389) — loan officers track personal/team KPIs
    Permission.scorecard.read,
    Permission.scorecard.create,
    Permission.scorecard.update,
    // Warehouse-line management (Issue #1631) — loan officers run multi-fund
    // sizing for borrowers, see attribution, and request exceptions.
    Permission.settings.warehouseLines.read,
    Permission.settings.warehouseLineProfiles.read,
    Permission.settings.warehouseLineGlobalRules.read,
    Permission.settings.capitalProviders.read,
    Permission.sizingRuns.read,
    Permission.sizingRuns.start,
    Permission.sizingRuns.select,
    // Issue #1645 — loan officers can unpin (e.g. when a case is reassigned
    // to a different fund). Manual-pin (D2) is intentionally NOT granted —
    // it bypasses sizer rule evaluation and is reserved for risk/admin.
    Permission.cases.unpinWarehouseLine,
    Permission.warehouseLineExceptions.read,
    Permission.warehouseLineExceptions.request,
    Permission.caseConditionsToFund.read,
    // Issue #1483 Gap 2/6 — loan view + cascade-resource reads;
    // originators draft credit memos and own borrower-exit writes;
    // loan officers also manage closing vendors (Issue #1470).
    Permission.loan.view,
    Permission.creditMemo.read,
    Permission.creditMemo.write,
    Permission.creditMemo.submit,
    Permission.caseComparable.read,
    Permission.borrowerExit.read,
    Permission.borrowerExit.write,
    Permission.releaseSchedule.read,
    Permission.borrowerTier.read,
    Permission.caseVendor.read,
    Permission.caseVendor.write,
    Permission.applicabilityRule.read,
    // Close.com integration (Issue #1670 Phase 2.4 — Gap 6.1) — loan officers
    // work directly with borrowers synced from Close, so they need to click
    // "Refresh from Close" on the borrower detail page and to inspect the
    // field-mapping configuration their admin set up. Configure + backfill
    // stay admin-only (those affect the connection itself, not per-borrower
    // operations).
    Permission.closeConsumer.refresh,
    Permission.closeConsumer.merge,
    Permission.closeFieldMapping.read,
    // Loan-stage catalog + assessment view (Issue #1749 follow-up) — loan
    // officers originate cases and need the stage indicator on every loan
    // they open. Assessment view supports underwriting hand-off context.
    Permission.settings.loanStages.read,
    Permission.settings.loanStageTransitions.read,
    Permission.assessment.view,
  ],

  // Consumer / Borrower - consumer portal users (loan applicants, end consumers)
  // DB stores role as 'borrower'; UserRole enum maps to 'consumer'. Both keys needed.
  // Ownership enforced at router level (consumerCases, consumerDocuments, etc.)
  consumer: CONSUMER_PERMISSIONS,
  borrower: CONSUMER_PERMISSIONS,

  // Partner - external partner with limited access
  partner: [
    Permission.cases.read, // Own referred cases only (enforced at service layer)
    Permission.consumers.read, // Own referrals only
    Permission.documents.read, // Own referred case documents only
    Permission.notifications.read,
    Permission.partners.read,
    Permission.partners.agreements.read,
    Permission.analytics.read, // Own referral analytics
    Permission.templates.read,
  ],
};

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get permissions for a user role
 */
export function getPermissionsForUserRole(role: string): PermissionValue[] {
  return USER_ROLE_PERMISSIONS[role] || [];
}

/**
 * Check if a role has a specific permission (for API Gateway resources)
 */
export function hasUserRolePermission(role: string, permission: PermissionValue): boolean {
  const permissions = getPermissionsForUserRole(role);
  return permissions.includes(Permission.all) || permissions.includes(permission);
}

/**
 * Check if a permission string matches a permission value.
 * Handles wildcard (*) permission.
 */
export function matchesPermission(userPermissions: string[], required: PermissionValue): boolean {
  return userPermissions.includes(Permission.all) || userPermissions.includes(required);
}
